import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { TrendingUp, X } from "lucide-react";

/** Shows a soft banner when a Trial company is above 80% of any quota. */
export default function UpsellBanner() {
  const { activeCompanyId } = useAuth();
  const [usage, setUsage] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(false);
    if (!activeCompanyId) { setUsage(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/billing/usage/${activeCompanyId}`);
        setUsage(data);
      } catch { setUsage(null); }
    })();
  }, [activeCompanyId]);

  if (!usage || usage.tier !== "trial" || dismissed) return null;
  const pct = (used, limit) => (limit === 0 ? 0 : used / limit);
  const worst = Math.max(
    pct(usage.emails.used, usage.emails.limit),
    pct(usage.ai_calls.used, usage.ai_calls.limit),
    pct(usage.mailboxes.used, usage.mailboxes.limit),
  );
  if (worst < 0.8) return null;

  const which =
    pct(usage.emails.used, usage.emails.limit) === worst ? "monthly emails" :
    pct(usage.ai_calls.used, usage.ai_calls.limit) === worst ? "AI calls" :
    "mailboxes";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
        className="mx-6 lg:mx-10 mt-6 card-3d p-4 flex items-center gap-4 border-l-4 border-l-sun bg-gradient-to-r from-sun/15 to-emerald-brand/10"
        data-testid="upsell-banner"
      >
        <div className="h-10 w-10 rounded-xl bg-sun/25 text-yellow-700 grid place-items-center shrink-0">
          <TrendingUp className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">You&apos;re at {Math.round(worst * 100)}% of your {which} quota</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Upgrade to <span className="text-emerald-brand font-semibold">Pro</span> for 15 mailboxes, 10,000 emails/mo, and 5,000 AI calls/mo — or contact sales for Enterprise.
          </div>
        </div>
        <Link
          to="/billing"
          className="pill bg-emerald-brand text-white shadow-md shadow-primary/25 hover:shadow-lg transition-shadow"
          data-testid="upsell-cta"
        >
          Upgrade →
        </Link>
        <button onClick={() => setDismissed(true)} className="text-muted-foreground hover:text-foreground" aria-label="Dismiss"><X className="h-4 w-4" /></button>
      </motion.div>
    </AnimatePresence>
  );
}
